import React, { FunctionComponent, useCallback, useState } from "react";
import { useIntl } from "react-intl";
import {
  Badge,
  Button,
  Heading,
  IconButton,
  Spinner,
  Stack,
  Table,
  TableProps,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react";
import { CheckIcon, CheckCircleIcon } from "@chakra-ui/icons";

import { useMutation } from "@apollo/client";
import { ACK_ALARM_TYPES_MUTATION } from "graphql/mutations";
import {
  AckAlarmTypes,
  AckAlarmTypesVariables,
  SiteAlarmType_siteAlarmType_history,
} from "graphql/generated";

import { Timestamp, RelativeTimestamp } from "common/hooks/time";

import NotificationIcon from "../notifications/NotificationIcon";


interface IProps extends TableProps {
  siteId: string;
  conditionId: string;
  items: SiteAlarmType_siteAlarmType_history[] | undefined;
  historyLength: number;
  timezone: string;
}


const AlarmHistory: FunctionComponent<IProps> = ({
  siteId,
  conditionId,
  historyLength,
  items,
  timezone,
  ...rest
}) => {
  const intl = useIntl();
  const textColor = useColorModeValue("black", "white");
  const bgColor = useColorModeValue("white", "black");
  const [relativeTimestamp, setRelativeTimestamp] = useState<boolean>(false);
  const toggleTimestampFormat = useCallback(() => setRelativeTimestamp(!relativeTimestamp), [relativeTimestamp]);
  const [ ackAlarmIds, setAckAlarmIds ] = useState<Set<string>>(new Set());
  const [ ackedAlarmIds, setAckedAlarmIds ] = useState<Set<string>>(new Set());
  const [ ackAlarms, setAckAlarms ] = useState<boolean>(false);
  const [ ackAlarmTypesMutation, { loading: saving } ] = useMutation<AckAlarmTypes, AckAlarmTypesVariables>(ACK_ALARM_TYPES_MUTATION);

  const toggleAlarmAck = useCallback((alarm: SiteAlarmType_siteAlarmType_history) => {
    const ids = ackAlarmIds;

    if (!ids.has(alarm.id)) {
      ids.add(alarm.id);
    } else {
      ids.delete(alarm.id);
    }

    setAckAlarmIds(ids);
    setAckAlarms(!ackAlarms);
  }, [ackAlarmIds, ackAlarms]);

  const ackAlarmTypes = useCallback(() => {
    const alarmTypeIds = Array.from(ackAlarmIds);

    ackAlarmTypesMutation({
      variables: {
        siteId,
        conditionId,
        alarmTypes: {
          alarmTypeIds,
        },
      },
      onCompleted: () => {
        const newAcked = new Set(Array.from(ackedAlarmIds));
        if (ackAlarmIds.size > 0) {
          ackAlarmIds.forEach((id) => newAcked.add(id));
        } else if (items) {
          items.forEach((item) => newAcked.add(item.id));
        }
        setAckedAlarmIds(newAcked);
        ackAlarmIds.clear();
        setAckAlarms(!ackAlarms);
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ackAlarmIds, ackAlarms]);

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
      >
        <Heading
          flexGrow={1}
        >
          {intl.formatMessage({
            id: "alarms__alarm_type__history",
            defaultMessage: "History",
          })}
          { items === undefined
          ? <Spinner size="sm" ml={2} />
          : <Badge fontSize="sm" bg={textColor} color={bgColor} borderRadius={15} px={2} py={1} ml={2} title={intl.formatMessage({
              id: "alarms__alarm_type__history_number_of",
              defaultMessage: "Number of previous incidents",
            })}>
              {historyLength}
            </Badge>
          }
        </Heading>
        {!!historyLength && <Button
          size="sm"
          colorScheme="enposol"
          onClick={ackAlarmTypes}
          leftIcon={saving ? <Spinner size="xs"/> : undefined}
        >
          {
            ackAlarmIds.size > 0
            ? intl.formatMessage({
              id: "alarms__history__acknowledge_selected",
              defaultMessage: "Acknowledge selected {count}",
            }, {
              count: ackAlarmIds.size
            })
            : intl.formatMessage({
              id: "alarms__history__acknowledge_all",
              defaultMessage: "Acknowledge all",
            })
          }
        </Button>}
      </Stack>
      {
        (
          !!items?.length && <Table variant="striped" size="sm" {...rest}>
            <Thead>

              <Tr>
                <Th>
                  {intl.formatMessage({
                    id: "alarms__history__column__level",
                    defaultMessage: "Level",
                  })}
                </Th>
                <Th width="90%">
                  {intl.formatMessage({
                    id: "alarms__history__column__text",
                    defaultMessage: "Message",
                  })}
                </Th>
                <Th>
                  {intl.formatMessage({
                    id: "alarms__history__column__count",
                    defaultMessage: "Count",
                  })}
                </Th>
                <Th>
                  {intl.formatMessage({
                    id: "alarms__history__column__timestamp_end",
                    defaultMessage: "Incident end",
                  })}
                </Th>
                <Th />
              </Tr>

            </Thead>
            <Tbody>

              {
                items.map((alarm) => {
                  return (
                    <Tr key={alarm.id}>
                      <Td whiteSpace="nowrap" title={
                        intl.formatMessage({
                          id: "alarms__history__column__level_icon",
                          defaultMessage: "Level {level}",
                        }, {
                          level: alarm.level
                        })
                      }>
                        <NotificationIcon alarmType={alarm.level} onlyActive={false} />
                      </Td>
                      <Td title={
                        intl.formatMessage({
                          id: "alarms__history__column__text",
                          defaultMessage: "Message",
                        })
                      }>
                        {alarm.message}
                      </Td>
                      <Td whiteSpace="nowrap" title={
                        intl.formatMessage({
                          id: "alarms__history__column__count",
                          defaultMessage: "Count",
                        })
                      }>
                        <Badge fontSize="xs" bg={textColor} color={bgColor} opacity={0.5} borderRadius="full" px={1} py={0}>
                          {alarm.count}
                        </Badge>
                      </Td>
                      <Td whiteSpace="nowrap" onClick={toggleTimestampFormat} title={
                        intl.formatMessage({
                          id: "alarms__history__column__timestamp_end",
                          defaultMessage: "Incident end",
                        })
                      }>
                        {
                          relativeTimestamp
                          ? alarm.endTime && <RelativeTimestamp date={`${alarm.endTime}+00:00`} />
                          : alarm.endTime && <Timestamp date={`${alarm.endTime}+00:00`} />
                        }
                      </Td>
                      <Td whiteSpace="nowrap" textAlign="center">
                        <IconButton
                          aria-label="Ack button"
                          icon={alarm.ack || ackedAlarmIds.has(alarm.id) || ackAlarmIds.has(alarm.id) ? <CheckIcon/> : <CheckCircleIcon/>}
                          disabled={alarm.ack || ackedAlarmIds.has(alarm.id)}
                          size="xs"
                          rounded="full"
                          colorScheme={alarm.ack || ackedAlarmIds.has(alarm.id) || ackAlarmIds.has(alarm.id) ? "enposol" : undefined}
                          onClick={() => toggleAlarmAck(alarm)}
                          title={
                            alarm.ack || ackedAlarmIds.has(alarm.id)
                            ? intl.formatMessage({
                              id: "alarms__history__acknowledged_already",
                              defaultMessage: "Already acknowledged",
                            })
                            : intl.formatMessage({
                              id: "alarms__history__acknowledge",
                              defaultMessage: "Mark for acknowledgement",
                            })
                          }
                        >
                        </IconButton>
                      </Td>
                    </Tr>
                  );
                })
              }

            </Tbody>
          </Table>
        )
      }
    </>
  );
};

export default AlarmHistory;
